/**
 * Socket.io client manager for CruzControl.
 * Manages the WebSocket connection to the backend for real-time events
 * (car counts, alerts, controller status, user activity).
 */
import { io } from 'socket.io-client'
import { BACKEND_API_PLACEHOLDER } from '@/../environment.config'

let socket = null

/**
 * Connect to the Socket.io server with JWT auth.
 * @param {string} token - The Cognito ID token for authentication
 * @param {object} store - Vuex store instance for committing mutations
 * @returns {object} The socket instance
 */
export function connectSocket(token, store) {
  if (socket?.connected) {
    console.log('[Socket.io] Already connected')
    return socket
  }

  // Determine the server URL — same origin if BACKEND_API_PLACEHOLDER is empty
  const serverUrl = BACKEND_API_PLACEHOLDER || undefined

  console.log('[Socket.io] Connecting to:', serverUrl || '(same origin)')
  console.log('[Socket.io] Auth token present:', !!token)

  socket = io(serverUrl, {
    auth: { token },
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10000,
    reconnectionAttempts: Infinity,
    transports: ['websocket', 'polling']
  })

  // Connection lifecycle events
  socket.on('connect', () => {
    console.log('[Socket.io] Connected:', socket.id, '| transport:', socket.io.engine.transport.name)
    store.commit('setSocketConnected', true)
  })

  socket.io.engine?.on('upgrade', (transport) => {
    console.log('[Socket.io] Transport upgraded to:', transport.name)
  })

  socket.on('disconnect', (reason) => {
    console.warn('[Socket.io] Disconnected:', reason)
    store.commit('setSocketConnected', false)
  })

  socket.on('connect_error', (err) => {
    console.error('[Socket.io] Connection error:', err.message)
    store.commit('setSocketConnected', false)
    // Check if the error is specifically an auth failure
    if (err.message.toLowerCase() === "jwt expired" || err.message.toLowerCase() === "invalid token") {
      // Dispatch an action to handle the expired token
      store.dispatch('handleAuthError') 
    }
  })

  socket.io.on('reconnect_attempt', (attempt) => {
    console.log('[Socket.io] Reconnection attempt #' + attempt)
  })

  socket.io.on('reconnect', (attempt) => {
    console.log('[Socket.io] Reconnected after', attempt, 'attempts')
  })

  socket.io.on('reconnect_failed', () => {
    console.error('[Socket.io] Reconnection failed — giving up')
  })

  // Real-time event listeners
  socket.on('car_count', (data) => {
    store.commit('setLastCarCount', data)
  })

  socket.on('alert:new', (data) => {
    store.commit('addLiveAlert', data)
  })

  socket.on('alert:close', (data) => {
    store.commit('closeLiveAlert', data)
  })

  socket.on('controller:status', (data) => {
    store.commit('setLiveControllerStatus', data)
  })

  socket.on('activity', (data) => {
    store.commit('pushActivity', data)
  })

  // System announcements (tenant-wide + site-specific)
  socket.on('system:message', (data) => {
    if (data.action === 'created') {
      store.commit('addAnnouncement', data.announcement)
    } else if (data.action === 'updated') {
      store.commit('updateAnnouncement', data.announcement)
    } else if (data.action === 'deleted') {
      store.commit('removeAnnouncement', data.announcement?.id)
    }
  })

  // Direct messaging
  socket.on('chat:message', (data) => {
    store.commit('receiveChatMessage', data)
  })

  socket.on('chat:typing', (data) => {
    store.commit('setChatTyping', data)
  })

  socket.on('chat:read', (data) => {
    // console.log("[Socket.io] chat:read event received:", data)
    // data should contain { sender_id }
    // Normalize based on what your backend sends (usually reader_id, partner_id, or sender_id)
    const partnerId = data.reader_id || data.partner_id || data.sender_id
    if (partnerId) {
      // 1. Clear the sidebar unread count
      store.commit('markConversationRead', partnerId)
      // 2. Flip 'read_at' to now on the actual messages in the open chat body
      store.commit('markActiveMessagesAsRead', partnerId)
    }
  })

  return socket
}

/**
 * Join a site's room to receive events for that site.
 * @param {string} siteId - The site UUID to join
 */
export function joinSite(siteId) {
  if (socket?.connected && siteId) {
    socket.emit('join:site', { site_id: siteId })
    console.log('[Socket.io] Joining site:', siteId)
  }
}

/**
 * Leave a site's room to stop receiving events for that site.
 * @param {string} siteId - The site UUID to leave
 */
export function leaveSite(siteId) {
  if (socket?.connected && siteId) {
    socket.emit('leave:site', { site_id: siteId })
    console.log('[Socket.io] Leaving site:', siteId)
  }
}

/**
 * Disconnect from the Socket.io server.
 */
export function disconnectSocket() {
  if (socket) {
    socket.disconnect()
    socket = null
    console.log('[Socket.io] Disconnected and cleaned up')
  }
}

/**
 * Update the auth token for reconnection (e.g. after token refresh).
 * @param {string} newToken - The new Cognito ID token
 */
export function updateSocketToken(newToken) {
  if (socket) {
    socket.auth.token = newToken
  }
}

/**
 * Check if the socket is currently connected.
 * @returns {boolean}
 */
export function isSocketConnected() {
  return socket?.connected || false
}

/**
 * Emit a typing indicator to a specific user.
 * @param {string} recipientId - The user ID of the typing recipient
 * @param {boolean} isTyping - True if typing, false if stopped
 */
let typingTimeout = null
export function emitTyping(recipientId, isTyping = true) {
  if (!socket?.connected || !recipientId) return

  // If stopping, bypass the debounce, clear active timeouts, and emit instantly
  if (!isTyping) {
    if (typingTimeout) {
      clearTimeout(typingTimeout)
      typingTimeout = null
    }
    // console.log("[Socket.io] Stopping typing status to:", recipientId)
    socket.emit('chat:typing', { recipient_id: recipientId, is_typing: false })
    return
  }

  // Debounce: only emit once per second
  if (typingTimeout) return
  // console.log("[Socket.io] Typing status to:", recipientId)
  socket.emit('chat:typing', { recipient_id: recipientId, is_typing: true })
  typingTimeout = setTimeout(() => { typingTimeout = null }, 1000)
}

/**
 * Emit a read receipt when the current user views a conversation.
 * @param {string} senderId - The ID of the user who sent the messages being read
 */
export function emitReadReceipt(senderId) {
  if (!socket?.connected || !senderId) return
  socket.emit('chat:read', { sender_id: senderId })
}
